/**
 * @description
 * A modal dialog component used to preview and download project proposals.
 * It supports both Summary and Detailed proposal views and handles the logic
 * for selecting templates, rendering proposal content, saving selected options,
 * and downloading the proposal in PDF or Word format.
 *
 * @props
 * - open (boolean): Controls visibility of the modal.
 * - dialogClose (function): Callback to close the modal.
 * - projectName (string): Name of the project shown in the modal heading.
 * - customerName (string): Name of the customer.
 * - options (array): List of proposal options with selection flags.
 * - isVariation (boolean): Whether the proposal is for a variation.
 * - isUpdate (boolean): Controls whether to update the selected options.
 * - type (enum): Proposal type (SUMMARY or DETAILED).
 * - stageTotal (object): Aggregated stage-level cost data.
 * - optionTotal (object): Aggregated option-level or variation-level cost data.
 * - projectTotal (object): Total project cost.
 * - selectedOptionId (string): If provided, filters view to a single option.
 * - subTaskList (array): List of subtasks used in detailed proposals.
 *
 * @features
 * - Fetches template options from global settings based on proposal type.
 * - Automatically selects a default template based on context.
 * - Renders summary or detailed proposal content based on type.
 * - Handles saving selected proposal options (in case of variations).
 * - Provides buttons to download the proposal in PDF or Word format.
 *
 */
import React, { useEffect, useState } from 'react';
import {
  downloadProjectQuote,
  downloadProjectVariation,
} from '../../actions/project-management.action';
import Cookies from 'js-cookie';
import { getGlobalSettingTemplateTypes } from '../../actions/global-setting-template.action';
import { ProposalType } from '../../util/enums';
import SummaryProposal from '../../components/proposal/summary';
import DetailedProposal from '../../components/proposal/detailed';
import { useDispatch } from 'react-redux';
import ProposalModal from './proposal-modal';
import SummaryVariationProposal from 'src/components/proposal/variation/summary';
import { useSelector } from 'react-redux';
import DetailedVariationProposal from 'src/components/proposal/variation/detailed';
function ProposalDialog(props) {
  const {
    open,
    dialogClose,
    projectName,
    customerName,
    options,
    isVariation,
    type = ProposalType.SUMMARY,
    stageTotal,
    optionTotal,
    projectTotal,
    selectedOptionId = '',
    subTaskList,
    viewType = 'full',
    stages = []
  } = props;
  const projectSummary = useSelector(state => state.projectSummary) || {}
  const project = projectSummary.project || {}
  const dispatch = useDispatch();

  let [templateOptions, setTemplateOptions] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [breakupView, setBreakupView] = useState('detailed');

  useEffect(() => {
    const fetchTemplates = async () => {
      const typeParam = isVariation ? 'ProjectVariation' : 'ProjectQuote';
      const templateData = await dispatch(
        getGlobalSettingTemplateTypes(typeParam)
      ).catch((e) => console.error(e));
      const templates = [];
      for (let key in templateData) {
        templates.push({
          value: templateData[key].id,
          key: templateData[key].name,
          type: templateData[key].type,
        });
      }
      setTemplateOptions(templates);
    };
    fetchTemplates();
  }, [isVariation, getGlobalSettingTemplateTypes]);

  useEffect(() => {
    if (Array.isArray(templateOptions)) {
      if (templateOptions.length === 1) {
        setDefaultTemplate(templateOptions[0]);
      } else if (templateOptions.length > 1) {
        let defaultOption;
        if (isVariation) {
          if (type === ProposalType.SUMMARY) {
            defaultOption = templateOptions.find(
              (option) => option.type === 'variation'
            );
          } else if (type === ProposalType.DETAILED) {
            defaultOption = templateOptions.find(
              (option) => option.type === 'detailed_variation'
            );
          }
        } else {
          if (type === ProposalType.SUMMARY) {
            defaultOption = templateOptions.find(
              (option) => option.type === 'project_quote'
            );
          } else if (type === ProposalType.DETAILED) {
            defaultOption = templateOptions.find(
              (option) => option.type === 'detailed_project_quote'
            );
          }
        }
        if (defaultOption) {
          setDefaultTemplate(defaultOption);
        } else {
          console.error('No matching default template found', templateOptions);
        }
      }
    }
  }, [templateOptions, type, isVariation]);

  const downloadFile = async (fileFormat = 'pdf', tempId) => {
    const filteredOptions = options.filter((option) => option.selected);
    const downloadFn = isVariation
      ? downloadProjectVariation
      : downloadProjectQuote;
    let downloadID = '';
    const breakupViewType = type === ProposalType.DETAILED ? breakupView : 'summary';
    if (isVariation) {
      if (Array.isArray(filteredOptions) && filteredOptions.length > 0) {
        await Promise.all(
          filteredOptions.map(async (item) => {
            downloadID = item.id;
            if (item.selected) {
              await dispatch(downloadFn(downloadID, fileFormat, tempId, viewType)).catch((e) => console.error(e));
            }
          })
        );
      }
    } else {
      downloadID = Cookies.get('projectId');
      await dispatch(downloadFn(downloadID, fileFormat, tempId, breakupViewType)).catch((e) => { throw e });
    }
  };
  const directDownload = (fileFormat) => {
    if (!defaultTemplate) {
      console.error('No default template selected');
      return;
    }
    downloadFile(fileFormat, defaultTemplate.value);
  };

  const singleOpt = isVariation ? optionTotal[selectedOptionId] : optionTotal;
  const singleOptionTotal = {
    ...(isVariation ? { [selectedOptionId]: singleOpt } : optionTotal || {}),
    projectTotal: isVariation ? singleOpt : optionTotal.projectTotal,
  };


  return (<>
    <ProposalModal
      open={open}
      onClose={dialogClose}
      modalHeading={`Proposal Name: ${projectName}`}
      fullWidth={true}
      maxWidth={'md'}
      onPdfClick={() => directDownload('pdf')}
      onWordClick={() => directDownload('docx')}
      backdropClick={true}
      isVariation={isVariation}
      showBreakupToggle={type === ProposalType.DETAILED && !isVariation}
      breakupView={breakupView}
      onBreakupViewChange={setBreakupView}
    >

      <div id='proposal' data-cy={viewType === 'tasks_only' ? 'tasks-only-view' : 'full-breakdown-view'}>
        <div className="d-flex justify-content-between mb-4">
          <div className="text-left proposal-address-block">
            <div className="view-block">
              <label>Customer Name</label>
              <p>{customerName}</p>
            </div>
          </div>
        </div>
        {type == ProposalType.SUMMARY && isVariation && (
          <SummaryVariationProposal
            optionsData={options}
            projectTotal={projectTotal}
            isVariation={isVariation}
            stageTotal={stageTotal}
            stages={stages}
            optionTotal={{
              [selectedOptionId]: optionTotal[selectedOptionId],
              projectTotal: optionTotal[selectedOptionId]
            }}
          />
        )}

        {type == ProposalType.SUMMARY && !isVariation && (
          <SummaryProposal
            optionsData={options}
            projectTotal={projectTotal}
            isVariation={isVariation}
            stageTotal={stageTotal}
            stages={stages}
            optionTotal={{
              [selectedOptionId]: optionTotal[selectedOptionId],
              projectTotal: optionTotal[selectedOptionId]
            }}
          />
        )}

        {type == ProposalType.DETAILED && isVariation && (
          <DetailedVariationProposal
            optionsData={options}
            projectTotal={projectTotal}
            isVariation={isVariation}
            stageTotal={stageTotal}
            stages={stages}
            optionTotal={singleOptionTotal}
            subTaskList={subTaskList}
            selectedOptionId={selectedOptionId}
            viewType={viewType}
            projectType={project?.projectType}
          />
        )}

        {type == ProposalType.DETAILED && !isVariation && (
          <DetailedProposal
            optionsData={options}
            projectTotal={projectTotal}
            isVariation={isVariation}
            stageTotal={stageTotal}
            stages={stages}
            optionTotal={singleOptionTotal}
            subTaskList={subTaskList}
            selectedOptionId={selectedOptionId}
            viewType={viewType}
            breakupView={breakupView}
            projectType={project?.projectType}
          />
        )}
      </div>
    </ProposalModal>
  </>)
}

export default ProposalDialog;